import { InventoryService } from "domain/Inventory";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Spinner } from "shared";
import { useCensusContext } from "../../../Census";
import { InventoryCensusService } from "domain/Census/services/inventory-census.service";
import { FieldRow, FeedbackSection, ScanHeader, CensusActions } from "./components";
import { SCAN_TRANSLATIONS } from "./translations";

import "./scan.page.scss";

export const ScanPage = () => {
  const [item, setItem] = useState();
  const [loading, setLoading] = useState(true);
  const [censusMessage, setCensusMessage] = useState("");
  const [alreadyCensused, setAlreadyCensused] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [language, setLanguage] = useState("en");

  // Field marking state
  const [markedFields, setMarkedFields] = useState({
    label: false,
    serialNumber: false,
    assignedTo: false,
    city: false,
    address: false,
    floor: false,
    office: false,
  });
  const [userComment, setUserComment] = useState("");

  const { id } = useParams();
  const { activeCensus, getCensusInventories } = useCensusContext();

  // Get current translations
  const t = useMemo(() => SCAN_TRANSLATIONS[language], [language]);

  // Derived state
  const hasMarkedFields = useMemo(() => Object.values(markedFields).some(Boolean), [markedFields]);

  useEffect(() => {
    if (!id) return;
    InventoryService.get({ itemId: id })
      .then((data) => setItem(data || null))
      .catch(() => setItem(null));
  }, [id]);

  useEffect(() => {
    if (!activeCensus) {
      setLoading(false);
      return setCensusMessage(t.noActiveCensus);
    }
    const queryParams = { itemId: id };
    getCensusInventories(activeCensus.id, queryParams)
      .then((data) => {
        const isCensusedInventory = !!data[0] && data[0].isMatching !== null;
        setAlreadyCensused(isCensusedInventory);
        const message = isCensusedInventory
          ? `${t.alreadyCensused} (${activeCensus.year})`
          : `${t.willBeAdded} (${activeCensus.year})`;
        setCensusMessage(message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [getCensusInventories, id, activeCensus, item, t]);

  const compileDescription = useCallback(
    (markedFields, userComment) => {
      const markedFieldLabels = Object.entries(markedFields)
        .filter(([_, marked]) => marked)
        .map(([field, _]) => t[field])
        .join(", ");

      if (!markedFieldLabels && !userComment.trim()) {
        return "-";
      }

      let description = "";
      if (markedFieldLabels) {
        description = `${t.problematicFields} ${markedFieldLabels}`;
      }
      if (userComment.trim()) {
        description += description ? `\n${t.comment} ${userComment}` : `${t.comment} ${userComment}`;
      }

      return description || "-";
    },
    [t]
  );

  const compileMarkedFieldLabels = useCallback(() => {
    return Object.entries(markedFields)
      .filter(([_, marked]) => marked)
      .map(([field, _]) => t[field])
      .join(", ");
  }, [markedFields, t]);

  const submitHandle = (isMatching) => {
    if (isMatching) {
      setMarkedFields(
        Object.keys(markedFields).reduce((acc, key) => {
          acc[key] = false;
          return acc;
        }, {})
      );
      setUserComment("");
    } else {
      // Validate if user is reporting issues but nothing marked
      if (!Object.values(markedFields).some(Boolean) && !userComment.trim()) {
        alert(t.markFieldsOrComment);
        return;
      }
    }

    setIsSubmitting(true);
    const itemForCensus = {
      inventoryId: item.id,
      censusId: activeCensus.id,
      isMatching,
      description: isMatching ? "-" : compileDescription(markedFields, userComment),
    };

    InventoryCensusService.addItem(itemForCensus)
      .then(() => {
        alert(`${t.successfullyAdded} ${activeCensus?.year}`);
        setAlreadyCensused(true);
        // Clear marks and comments after successful submission
        setMarkedFields(
          Object.keys(markedFields).reduce((acc, key) => {
            acc[key] = false;
            return acc;
          }, {})
        );
        setUserComment("");
      })
      .catch(() => {
        alert(t.errorAdding);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleFieldToggle = useCallback((fieldKey, isMarked) => {
    setMarkedFields((prev) => ({
      ...prev,
      [fieldKey]: isMarked,
    }));
  }, []);

  if (loading || item === undefined) return <Spinner text={t.loadingContent} size="large" />;
  if (!item)
    return (
      <h2 className="scan__error">
        {t.noInventoryFound} {id}
      </h2>
    );

  return (
    <div className="scan">
      <ScanHeader item={item} currentLanguage={language} onLanguageChange={setLanguage} />

      <div className="scan__wrap">
        {/* Show census status only when no fields are marked */}
        {!hasMarkedFields && <div className="scan__census-status">{censusMessage}</div>}

        {/* Show problems banner when fields are marked */}
        {hasMarkedFields && (
          <div className="scan__info-message scan__info-message--warning">
            <p>
              <strong>{t.markedProblematicFields}</strong> {compileMarkedFieldLabels()}
            </p>
          </div>
        )}

        {/* Warning for items with parts */}
        {item.parts && item.parts.length > 0 && activeCensus && !alreadyCensused && (
          <div className="scan__info-message scan__info-message--warning">
            <p>{t.itemHasParts}</p>
          </div>
        )}

        {/* Item data with field rows */}
        <div className="scan__item-data">
          <FieldRow
            label={t.label}
            value={item.label}
            isMarked={markedFields.label}
            onToggle={handleFieldToggle}
            fieldKey="label"
            incorrectLabel={t.incorrect}
            markAsIncorrectLabel={t.markAsIncorrect}
            hideCheckbox={alreadyCensused}
          />
          <FieldRow
            label={t.serialNumber}
            value={item.serialNumber}
            isMarked={markedFields.serialNumber}
            onToggle={handleFieldToggle}
            fieldKey="serialNumber"
            incorrectLabel={t.incorrect}
            markAsIncorrectLabel={t.markAsIncorrect}
            hideCheckbox={alreadyCensused}
          />
          <FieldRow
            label={t.assignedTo}
            value={item.assignedTo}
            isMarked={markedFields.assignedTo}
            onToggle={handleFieldToggle}
            fieldKey="assignedTo"
            incorrectLabel={t.incorrect}
            markAsIncorrectLabel={t.markAsIncorrect}
            hideCheckbox={alreadyCensused}
          />
          <FieldRow
            label={t.city}
            value={item.location?.city}
            isMarked={markedFields.city}
            onToggle={handleFieldToggle}
            fieldKey="city"
            incorrectLabel={t.incorrect}
            markAsIncorrectLabel={t.markAsIncorrect}
            hideCheckbox={alreadyCensused}
          />
          <FieldRow
            label={t.address}
            value={item.location?.address}
            isMarked={markedFields.address}
            onToggle={handleFieldToggle}
            fieldKey="address"
            incorrectLabel={t.incorrect}
            markAsIncorrectLabel={t.markAsIncorrect}
            hideCheckbox={alreadyCensused}
          />
          <FieldRow
            label={t.floor}
            value={item.location?.floor}
            isMarked={markedFields.floor}
            onToggle={handleFieldToggle}
            fieldKey="floor"
            incorrectLabel={t.incorrect}
            markAsIncorrectLabel={t.markAsIncorrect}
            hideCheckbox={alreadyCensused}
          />
          <FieldRow
            label={t.office}
            value={item.location?.office}
            isMarked={markedFields.office}
            onToggle={handleFieldToggle}
            fieldKey="office"
            incorrectLabel={t.incorrect}
            markAsIncorrectLabel={t.markAsIncorrect}
            hideCheckbox={alreadyCensused}
          />
        </div>

        {/* Feedback section - shown when fields marked */}
        {hasMarkedFields && (
          <FeedbackSection
            userComment={userComment}
            onCommentChange={(e) => setUserComment(e.target.value)}
            additionalCommentLabel={t.additionalComment}
            commentPlaceholder={t.commentPlaceholder}
          />
        )}

        {/* Action buttons */}
        <CensusActions
          isHidden={!activeCensus || alreadyCensused || (item.parts && item.parts.length > 0)}
          hasMarkedFields={hasMarkedFields}
          isSubmitting={isSubmitting}
          onConfirm={() => submitHandle(true)}
          onReportIssues={() => submitHandle(false)}
          areDataCorrectLabel={t.areDataCorrect}
          allCorrectLabel={t.allCorrect}
          confirmProblemsLabel={t.confirmProblems}
        />
      </div>
    </div>
  );
};
